import axios, { AxiosResponse } from "axios";
import * as bootstrap from "bootstrap";
import { formValidation } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Declarative } from "@form-validation/plugin-declarative";

import "react-form-builder2/dist/app.css";

import { renderReactFormBuilder } from "./ReactRenderer";
import { Modal } from "bootstrap";

// ✅ New: expose React date picker interop functions
import * as crmDatePicker from "./crmDatePickerInterop";

namespace CopperDragonCRM {
    declare const JF: any;

    export class ClientApp {
        declare _currentValidator: any;

        constructor() {}

        public initializeClickInterceptor(): void {
            const linksInsideAccordion: NodeListOf<Element> =
              document.querySelectorAll(".accordion-button a");
            linksInsideAccordion.forEach((link: Element): void => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const target = event.target as HTMLAnchorElement;
                    window.open(target.href, "_blank");
                });
            });
        }

        // -----------------------------
        // FormValidation
        // -----------------------------
        public InitialiseForm(formId: string): boolean {
            const formElement: HTMLElement | null = document.getElementById(formId);
            if (!formElement) return false;

            let initialised: boolean =
              formElement.getAttribute("data-fv-initialized") === "true";

            if (!initialised) {
                this._currentValidator = formValidation(formElement, {
                    plugins: {
                        declarative: new Declarative(),
                        trigger: new Trigger(),
                        bootstrap: new Bootstrap5({
                            formClass: "fv-plugins-bootstrap5",
                            rowInvalidClass: "is-invalid",
                            rowPattern: /^.*(col|row|form-group|fv-plugins-icon)$/,
                            rowValidClass: "is-valid",
                            rowSelector: ".form-group",
                        }),
                    },
                });

                formElement.setAttribute("data-fv-initialized", "true");
                initialised = true;
            }

            return initialised;
        }

        public ValidateForm(formId: string): Promise<boolean> {
            return new Promise((resolve) => {
                if (this._currentValidator && this._currentValidator.form.id === formId) {
                    this._currentValidator.validate().then((validResult: any): void => {
                        resolve(validResult === "Valid");
                    });
                } else {
                    resolve(false);
                }
            });
        }

        // -----------------------------
        // Bootstrap modals
        // -----------------------------
        public ShowModal(modalId: string): void {
            const modal: HTMLElement | null = document.getElementById(modalId);
            if (modal) {
                const m = bootstrap.Modal.getOrCreateInstance(modal);
                m.show();
            }
        }

        public HideModal(modalId: string): void {
            const modal: HTMLElement | null = document.getElementById(modalId);
            if (modal) {
                const m: Modal | null = bootstrap.Modal.getInstance(modal);
                m?.hide();
            }
        }

        public DismissModal(modalId: string): void {
            const modal: HTMLElement | null = document.getElementById(modalId);
            if (modal) {
                const m: Modal | null = bootstrap.Modal.getInstance(modal);
                m?.hide();
            }
        }

        // -----------------------------
        // Action details UI
        // -----------------------------
        public ShowActionDetails(): boolean {
            const elem: HTMLElement | null = document.getElementById(
              "action-details-container"
            );
            if (!elem) return false;

            elem.classList.toggle("action-details-show");
            elem.classList.toggle("action-details-hide");
            return true;
        }

        public HideActionDetails(): boolean {
            const elem: HTMLElement | null = document.getElementById(
              "action-details-container"
            );
            if (!elem) return false;

            elem.classList.remove("action-details-show");
            elem.classList.add("action-details-hide");
            return true;
        }

        // -----------------------------
        // JotForm
        // -----------------------------
        public JotFormLogin(username: string, password: string): Promise<any> {
            return new Promise((resolve) => {
                JF.userLogin({ username, password }, (): void => resolve(true));
            });
        }

        public GetIFrameSource(formUrl: string): Promise<string> {
            return axios
              .get(formUrl)
              .then((result: AxiosResponse) =>
                result.status >= 200 && result.status <= 299 ? result.data : ""
              )
              .catch(() => "");
        }

        public GetFromStatic(formId: string, details: any): Promise<string> {
            return Promise.resolve(`/static/jotform/${formId}/${formId}.html`);
        }

        public LoadPrePopulatedForm(url: string): void {
            const frm = document.getElementById("orderform") as HTMLFrameElement;
            if (frm) frm.src = url;
        }

        public LoadMergeForm(formId: string, details: any, fieldmaps: any): void {
            try {
                const path = `/JotForm/Form/${formId}`;
                const frm = document.getElementById("orderform") as HTMLFrameElement;

                if (details && fieldmaps && fieldmaps.length > 0 && frm) {
                    frm.hidden = true;
                    frm.src = path;

                    const formFldMap = fieldmaps.filter(
                      (map: any): boolean => map.formId === formId
                    );

                    frm.onload = (): void => {
                        const jfDoc: Document | null = frm?.contentWindow?.document ?? null;
                        if (!jfDoc) return;

                        const formContainer = jfDoc.getElementsByClassName(
                          "jotform-form"
                        )[0] as HTMLElement;

                        const link: HTMLLinkElement = document.createElement("link");
                        link.href = `${location.origin}/css/jotform-overrides.css`;
                        link.rel = "stylesheet";
                        link.type = "text/css";
                        jfDoc.head.appendChild(link);

                        formFldMap.forEach((fld: any): void => {
                            const docElement = jfDoc.getElementById(
                              fld.formField
                            ) as HTMLInputElement;

                            const detailProp = fld.detailProperty;

                            if (docElement && detailProp) {
                                docElement.value = details[detailProp];
                            }
                        });

                        frm.hidden = false;
                    };
                }
            } catch (err) {
                console.log(err);
            }
        }

        // -----------------------------
        // Utility / navigation
        // -----------------------------
        public ScrollToTop(element: string): void {
            const elem: HTMLElement | null = document.getElementById(element);
            elem ? (elem.scrollTop = 0) : window.scrollTo(0, 0);
        }

        public SetActiveTab(activeTabId: string): void {
            const selectedTabElem: HTMLElement | null =
              document.getElementById(activeTabId);
            if (selectedTabElem) {
                const tab = new bootstrap.Tab(selectedTabElem);
                tab.show();
            }
        }

        public ClearModal(modalId: string): void {
            const elem: HTMLElement | null = document.getElementById(modalId);
            if (!elem) console.log(`modal ${modalId} does not exist on document`);
        }

        public InitialiseUpload(...args: any[]): void {
            console.warn("InitialiseUpload requires a replacement for jQuery fileinput");
        }

        public DeleteFile(file: string): Promise<boolean> {
            return DotNet.invokeMethodAsync("CopperDragonCRM", "RemoveCustomerImage", file).then(
              (): boolean => true
            );
        }

        public ForceCollapse(id: string): void {
            const element: HTMLElement | null = document.getElementById(id);
            if (element) {
                const collapse = bootstrap.Collapse.getOrCreateInstance(element);
                collapse.hide();
            }
        }

        public PrintDiv(divId: string): void {
            const prDoc: HTMLElement | null = document.getElementById(divId);
            if (!prDoc) return;

            const pr: Window | null = window.open("", "");
            if (!pr) return;

            const doc: Document = pr.document;

            // Create structure
            const html: HTMLHtmlElement = doc.createElement("html");
            const head: HTMLHeadElement = doc.createElement("head");
            const body: HTMLBodyElement = doc.createElement("body");

            // Base tag (important for relative URLs)
            const base: HTMLBaseElement = doc.createElement("base");
            base.href = `${location.origin}${location.pathname}`;
            head.appendChild(base);

            // Stylesheets
            const stylesheets: string[] = [
                `${location.origin}/lib/bootstrap/css/bootstrap.min.css`,
                `${location.origin}/lib/fontawesome/css/all.min.css`,
                `${location.origin}/css/site.css`,
            ];

            stylesheets.forEach((href) => {
                const link: HTMLLinkElement = doc.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                head.appendChild(link);
            });

            // Inline <style> blocks from current document
            document.querySelectorAll("style").forEach((style) => {
                head.appendChild(style.cloneNode(true));
            });

            // Content
            body.innerHTML = prDoc.innerHTML;

            // Assemble document
            html.appendChild(head);
            html.appendChild(body);

            // Replace document safely
            doc.open();
            doc.replaceChild(html, doc.documentElement);
            doc.close();

            pr.focus();
        }

        public renderFormBuilder(elementId: string): void {
            renderReactFormBuilder(elementId);
        }
    }
}

// ----------------------------------------------------
// Global exposure for Blazor JS interop
// ----------------------------------------------------
declare global {
    interface Window {
        copperdragonapp: any;
    }
}

window.copperdragonapp = new CopperDragonCRM.ClientApp();

// Expose the new date picker API for the reusable Blazor component
window.copperdragonapp.crmDatePicker = {
    mount: crmDatePicker.mount,
    unmount: crmDatePicker.unmount,
    set: crmDatePicker.set
};